import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import config from '../config.js';
import pinPrompt from '../utils/pinPrompt';
import { apiErrorMessage } from '../utils/apiError';

const API_BASE = config.API_BASE_URL;

export default function CustomerPOQueuePage() {
  // State for the list of pending POs
  const [pendingPOs, setPendingPOs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for the upload form
  const [customerList, setCustomerList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // State for document preview
  const [previewDoc, setPreviewDoc] = useState(null);

  // Load initial data on component mount
  useEffect(() => {
    loadPendingPOs();
    loadCustomers();
  }, []);

  async function loadPendingPOs() {
    setLoading(true);
    setError(null);
    try {
      // NOTE: The backend now returns JobDocumentResponse objects
      const res = await fetch(`${API_BASE}/customer_pos?status=Pending Entry`);
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Failed to load pending POs"));
      }
      const data = await res.json();
      setPendingPOs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadCustomers() {
    try {
      const res = await fetch(`${API_BASE}/customers`);
      if (!res.ok) throw new Error("Failed to load customers");
      const data = await res.json();
      setCustomerList(data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error(err);
    }
  }

  function onFileChange(e) {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  }

  async function handleUpload() {
    if (!selectedFile) {
      alert("Please select a file to upload.");
      return;
    }
    if (!selectedCustomerId) {
      alert("Please select a customer for this Purchase Order.");
      return;
    }
    // Logic allows optional PIN, prompt if your flow requires it, otherwise omit
    const userPin = await pinPrompt("Enter your 4-digit PIN to confirm the upload:");
    if (!userPin) return; 

    setUploadError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("pin", userPin);
      formData.append("customer_id", selectedCustomerId);

      const res = await fetch(`${API_BASE}/customer_pos/upload`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Upload failed"));
      }

      await res.json();
      alert("Customer PO uploaded successfully and added to the queue.");

      // Reset form and refresh list
      setSelectedFile(null);
      setSelectedCustomerId('');
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      loadPendingPOs();

    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(po) {
    // UPDATED: Access document_id
    if (!window.confirm(`Are you sure you want to delete PO from "${po.customer_name}" (${po.original_filename})? This cannot be undone.`)) {
      return;
    }
    try {
      // UPDATED: URL uses document_id
      const res = await fetch(`${API_BASE}/customer_pos/${po.document_id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Failed to delete"));
      }
      alert("Customer PO deleted successfully.");
      // Refresh the list
      loadPendingPOs();
      // Clear preview if it's the deleted doc
      if (previewDoc && previewDoc.document_id === po.document_id) {
        setPreviewDoc(null);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  }

  // PO files are private in S3; the list's signed links expire after an hour,
  // and this page can stay open longer than that — so re-sign on open.
  const openPreview = async (po) => {
    setPreviewDoc(po);
    try {
      const res = await fetch(`${API_BASE}/customer_pos/${po.document_id}`);
      if (!res.ok) return;
      const fresh = await res.json();
      if (fresh && fresh.s3_url) {
        setPreviewDoc((cur) => (cur && cur.document_id === po.document_id ? { ...cur, s3_url: fresh.s3_url } : cur));
      }
    } catch (e) {
      // keep the list's link
    }
  };

  const renderPreview = () => {
    if (!previewDoc) return null;

    const docURL = previewDoc.s3_url;
    const isPDF = previewDoc.s3_key.toLowerCase().endsWith(".pdf");

    return (
      <div style={{ marginTop: "2rem" }}>
        <h4>Document Preview</h4>
        <div style={{ marginBottom: "0.5rem" }}>
          <strong>
            {/* UPDATED: Access document_id */}
            PO #{previewDoc.document_id} - {previewDoc.original_filename}
          </strong>
        </div>
        {isPDF ? (
          <embed src={docURL} type="application/pdf" width="100%" height="800px" />
        ) : (
          <img src={docURL} alt={previewDoc.original_filename} style={{ maxWidth: "100%", height: "auto" }} />
        )}
      </div>
    );
  };

  return (
    <div className="container my-4">
      <h3>Customer Purchase Order Queue</h3>
      <p>
        Upload customer POs here to add them to the "Pending Entry" queue. Select a PO from the queue to begin creating jobs.
      </p>

      {/* Upload Form */}
      <div className="card mb-4">
        <div className="card-body">
          <h5>Upload New Customer PO</h5>
          <div className="mb-3">
            <label className="form-label">Customer</label>
            <select
              className="form-select"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
              disabled={uploading}
            >
              <option value="">-- Select a Customer --</option>
              {customerList.map((c) => (
                <option key={c.customer_id} value={c.customer_id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Select PO File (Image or PDF)</label>
            <input
              type="file"
              className="form-control"
              onChange={onFileChange}
              ref={fileInputRef}
              disabled={uploading}
            />
          </div>
          {selectedFile && <div className="mb-2"><strong>Selected:</strong> {selectedFile.name}</div>}
          <button
            className="btn btn-primary"
            onClick={handleUpload}
            disabled={!selectedFile || !selectedCustomerId || uploading}
          >
            {uploading ? "Uploading..." : "Upload and Add to Queue"}
          </button>
          {uploadError && <div className="alert alert-danger mt-3">{uploadError}</div>}
        </div>
      </div>

      {/* List of Pending POs */}
      <div className="card">
        <div className="card-body">
          <h5>Pending Entry Queue</h5>
          {loading && <div>Loading...</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && pendingPOs.length === 0 && <div>No pending POs found.</div>}
          {!loading && pendingPOs.length > 0 && (
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Customer</th>
                  <th>Filename</th>
                  <th>Uploaded By</th>
                  <th>Uploaded At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingPOs.map((po) => (
                  // UPDATED: Key uses document_id
                  <tr key={po.document_id}>
                    <td>{po.document_id}</td>
                    <td>{po.customer_name || 'N/A'}</td>
                    <td>{po.original_filename}</td>
                    <td>{po.uploaded_by_name || 'N/A'}</td>
                    <td>{new Date(po.uploaded_at).toLocaleString()}</td>
                    <td>
                      <Link
                        to="/job-entry"
                        state={{ customerPO: po }}
                        className="btn btn-sm btn-success me-2"
                      >
                        Create Job
                      </Link>
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => openPreview(po)}
                      >
                        Preview
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(po)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Inline Document Preview at the Bottom */}
      {renderPreview()}
    </div>
  );
}